import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DashboardPageFormBody,
  DashboardPageFormField,
  DashboardPageFormToggleSection,
} from '@shigo-ui/dashboard-ui';
import { Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@shigo-ui/ui';

import { ChoiceCardField } from '../../../../../components/forms/ChoiceCardField';
import { dashboardT } from '../../../../../lib/messages';
import { PublisherCostType, PublisherCpmType } from '../../model';
import type { PublisherFormValue } from '../../publisherFormSchema';

/** Form-only "none" choice; it is stored as an empty cost type. */
enum PublisherPricingChoice {
  None = 'NONE',
}

function revenueTypeOptions() {
  return [
    {
      value: PublisherPricingChoice.None,
      label: dashboardT('publishers.none'),
      description: dashboardT('publishers.revenueTypeNoneHint'),
    },
    {
      value: PublisherCostType.RevenueShare,
      label: dashboardT('publishers.revenueShare'),
      description: dashboardT('publishers.revenueTypeRevenueShareHint'),
    },
    {
      value: PublisherCostType.Placement,
      label: dashboardT('publishers.placement'),
      description: dashboardT('publishers.revenueTypePlacementHint'),
    },
  ];
}

function saasFeeOptions() {
  return [
    {
      value: PublisherPricingChoice.None,
      label: dashboardT('publishers.none'),
      description: dashboardT('publishers.saasFeeNoneHint'),
    },
    {
      value: PublisherCostType.RevenueShare,
      label: dashboardT('publishers.revenueShare'),
      description: dashboardT('publishers.saasFeeRevenueShareHint'),
    },
    {
      value: PublisherCostType.Cpm,
      label: dashboardT('publishers.cpm'),
      description: dashboardT('publishers.saasFeeCpmHint'),
    },
  ];
}

export function PublisherPricingFields() {
  'use no memo';
  const { control } = useFormContext<PublisherFormValue>();
  const revenueCostType = useWatch({ control, name: 'revenueCostType' });
  const saasCostType = useWatch({ control, name: 'saasCostType' });
  return (
    <DashboardPageFormBody width="lg">
      <Controller
        control={control}
        name="revenueCostType"
        render={({ field }) => (
          <ChoiceCardField
            label={dashboardT('publishers.revenueType')}
            description={dashboardT('publishers.revenueTypeHint')}
            name={field.name}
            value={field.value || PublisherPricingChoice.None}
            options={revenueTypeOptions()}
            onValueChange={(value) => {
              field.onChange(value === PublisherPricingChoice.None ? '' : value);
            }}
            onBlur={field.onBlur}
          />
        )}
      />
      {revenueCostType === PublisherCostType.RevenueShare ? (
        <div className="grid gap-5 md:grid-cols-2">
          <Controller
            control={control}
            name="revenueShare"
            render={({ field, fieldState }) => (
              <DashboardPageFormField
                label={dashboardT('publishers.revenueShare')}
                htmlFor="publisher-revenue-share"
                required
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  id="publisher-revenue-share"
                  type="number"
                  required
                  aria-invalid={Boolean(fieldState.error)}
                />
              </DashboardPageFormField>
            )}
          />
          <Controller
            control={control}
            name="revenueShareIo"
            render={({ field, fieldState }) => (
              <DashboardPageFormField
                label={dashboardT('publishers.revenueShareIo')}
                htmlFor="publisher-revenue-share-io"
                error={fieldState.error?.message}
              >
                <Input
                  {...field}
                  id="publisher-revenue-share-io"
                  type="number"
                  aria-invalid={Boolean(fieldState.error)}
                />
              </DashboardPageFormField>
            )}
          />
        </div>
      ) : null}
      <div className="border-border space-y-5 border-t pt-5">
        <Controller
          control={control}
          name="saasCostType"
          render={({ field }) => (
            <ChoiceCardField
              label={dashboardT('publishers.saasFee')}
              description={dashboardT('publishers.saasFeeHint')}
              name={field.name}
              value={field.value || PublisherPricingChoice.None}
              options={saasFeeOptions()}
              onValueChange={(value) => {
                field.onChange(value === PublisherPricingChoice.None ? '' : value);
              }}
              onBlur={field.onBlur}
            />
          )}
        />
        {saasCostType ? (
          <div className="grid gap-5 md:grid-cols-2">
            {saasCostType === PublisherCostType.Cpm ? (
              <>
                <Controller
                  control={control}
                  name="saasCpmMetric"
                  render={({ field, fieldState }) => (
                    <DashboardPageFormField
                      label={dashboardT('publishers.saasCpmEvent')}
                      required
                      error={fieldState.error?.message}
                    >
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger aria-label={dashboardT('publishers.saasCpmEvent')}>
                          <SelectValue placeholder={dashboardT('publishers.select')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={PublisherCpmType.AdRequests}>
                            {dashboardT('publishers.cpmMetric.adRequests')}
                          </SelectItem>
                          <SelectItem value={PublisherCpmType.FilledImpressions}>
                            {dashboardT('publishers.cpmMetric.filledImpressions')}
                          </SelectItem>
                          <SelectItem value={PublisherCpmType.AdImpressions}>
                            {dashboardT('publishers.cpmMetric.adImpressions')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </DashboardPageFormField>
                  )}
                />
                <Controller
                  control={control}
                  name="saasCpmValue"
                  render={({ field, fieldState }) => (
                    <DashboardPageFormField
                      label={dashboardT('publishers.saasCpmValue')}
                      htmlFor="publisher-saas-cpm-value"
                      required
                      error={fieldState.error?.message}
                    >
                      <Input
                        {...field}
                        id="publisher-saas-cpm-value"
                        type="number"
                        required
                        aria-invalid={Boolean(fieldState.error)}
                      />
                    </DashboardPageFormField>
                  )}
                />
              </>
            ) : null}
            {saasCostType === PublisherCostType.RevenueShare ? (
              <Controller
                control={control}
                name="saasRevenueShare"
                render={({ field, fieldState }) => (
                  <DashboardPageFormField
                    label={dashboardT('publishers.saasRevenueShare')}
                    htmlFor="publisher-saas-revenue-share"
                    required
                    error={fieldState.error?.message}
                  >
                    <Input
                      {...field}
                      id="publisher-saas-revenue-share"
                      type="number"
                      required
                      aria-invalid={Boolean(fieldState.error)}
                    />
                  </DashboardPageFormField>
                )}
              />
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="grid gap-5 md:grid-cols-2">
        <Controller
          control={control}
          name="whitelistAppsEnabled"
          render={({ field }) => (
            <DashboardPageFormToggleSection
              label={dashboardT('publishers.whitelistApps')}
              checked={field.value}
              onCheckedChange={field.onChange}
            >
              <p className="text-muted-foreground text-sm">{dashboardT('publishers.enabled')}</p>
            </DashboardPageFormToggleSection>
          )}
        />
        <Controller
          control={control}
          name="whitelistDomainsEnabled"
          render={({ field }) => (
            <DashboardPageFormToggleSection
              label={dashboardT('publishers.whitelistDomains')}
              checked={field.value}
              onCheckedChange={field.onChange}
            >
              <p className="text-muted-foreground text-sm">{dashboardT('publishers.enabled')}</p>
            </DashboardPageFormToggleSection>
          )}
        />
      </div>
    </DashboardPageFormBody>
  );
}
